import {
  addMonths,
  addYears,
  differenceInMonths,
  isValid,
  parseISO,
  subMonths,
  subYears,
} from 'date-fns';
import { toISO8601LocalString } from 'utils/helpers';

type DateValue = string | null | undefined;
type DurationValue = number | null | undefined;

type LoanTerm = {
  years: number;
  months: number;
};

const parseLoanDate = (value: DateValue) => {
  if (!value) return null;

  const date = parseISO(value);
  return isValid(date) ? date : null;
};

export const hasValidLoanDate = (value: DateValue) => parseLoanDate(value) !== null;

const normalizeDuration = (years: DurationValue, months: DurationValue) => ({
  years: years ?? 0,
  months: months ?? 0,
});

const isValidDuration = (years: DurationValue, months: DurationValue) => {
  const duration = normalizeDuration(years, months);
  return (
    Number.isInteger(duration.years)
    && Number.isInteger(duration.months)
    && duration.years >= 0
    && duration.months >= 0
  );
};

export const estimateLastPaymentDate = (
  firstPaymentDate: DateValue,
  years: DurationValue,
  months: DurationValue,
): string | null => {
  const firstDate = parseLoanDate(firstPaymentDate);
  if (!firstDate || !isValidDuration(years, months)) return null;

  const duration = normalizeDuration(years, months);
  const lastDate = addMonths(addYears(firstDate, duration.years), duration.months);
  return toISO8601LocalString(lastDate, false);
};

export const estimateFirstPaymentDate = (
  lastPaymentDate: DateValue,
  years: DurationValue,
  months: DurationValue,
): string | null => {
  const lastDate = parseLoanDate(lastPaymentDate);
  if (!lastDate || !isValidDuration(years, months)) return null;

  const duration = normalizeDuration(years, months);
  const firstDate = subMonths(subYears(lastDate, duration.years), duration.months);
  return toISO8601LocalString(firstDate, false);
};

export const estimateLoanTerm = (
  firstPaymentDate: DateValue,
  lastPaymentDate: DateValue,
): LoanTerm | null => {
  const firstDate = parseLoanDate(firstPaymentDate);
  const lastDate = parseLoanDate(lastPaymentDate);
  if (!firstDate || !lastDate || lastDate < firstDate) return null;

  const totalMonths = differenceInMonths(lastDate, firstDate);
  return {
    years: Math.floor(totalMonths / 12),
    months: totalMonths % 12,
  };
};
